import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import cd from 'debug';
import { compose } from 'redux';
import { OrderedSet } from 'immutable';
import core, { App, Viewport } from '@gin-betting/core';
import matchList, { Pagination } from '@gin-betting/match-list';

import type { State } from 'types';
import {
    withBettingReadyIndicatorContext,
    BettingReadyIndicatorContextProps,
} from 'modules/betting-ready-indicator';
import { AsyncFreebetPanel } from 'modules/freebet/async';
import UpcomingMatchList from './UpcomingMatchList';
import PaginationWrapper from '../PaginationWrapper';
import './upcoming.scss';

const debug = cd('app:match-list:components:Upcoming');

const {
    utils: { isFetchFinished },
} = core;

const {
    utils: { getFiltersFromLocation },
    viewport: {
        selectors: { makeViewportSelector },
    },
} = core;
const {
    pagination: {
        actions: { dropPaginationState, setMinAndMaxPages },
        hocs: { withPagination },
        selectors: {
            selectDirection,
            selectIsInitialLoad,
            selectPaginationByLocation,
        },
    },
    selectors: { selectCategoryByLocation },
} = matchList;

interface MappedCommonProps extends Pagination.NeededPaginationProps {
    lessThanOrEqual: Viewport.Breakpoints;
}

interface MappedActions {
    dropPaginationState: Pagination.DropPaginationStateFn;
    setMinAndMaxPages: Pagination.SetMinAndMaxPagesFn;
}

type Props = MappedCommonProps &
    Partial<Pagination.ReturnedPaginationProps> &
    MappedActions &
    BettingReadyIndicatorContextProps;

class Upcoming extends React.Component<Props> {
    componentDidUpdate() {
        const {
            readyIndicator,
            categoryData: { fetchStatus },
        } = this.props;

        if (isFetchFinished(fetchStatus)) {
            readyIndicator.setReady();
        }
    }

    render() {
        debug('render', this.props);

        const {
            lessThanOrEqual,
            categoryData,
            hasNext,
            hasMore,
            isUp,
            matchIdsWithVisibilityFlags,
            visibilityInitRef,
            handleVisibilityChange,
            loadMoreWithPagination,
        } = this.props;

        const { fetchStatus } = categoryData;
        let { matchIds } = categoryData;

        const isMedium = lessThanOrEqual.medium;
        const isSmall = lessThanOrEqual.small;
        const { freeBetId: freebetId } = getFiltersFromLocation();

        return (
            <PaginationWrapper
                hasNext={hasNext}
                pollerName={App.PollerName.Upcoming}
                fetchStatus={fetchStatus}
                matchIdsSize={matchIds.size}
                setMinAndMaxPages={this.props.setMinAndMaxPages}
                /* for pagination */
                hasMore={hasMore}
                isUp={isUp}
                dropPaginationState={this.props.dropPaginationState}
                loadMoreWithPagination={loadMoreWithPagination}
                isMedium={isMedium}
            >
                {freebetId && (
                    <div styleName="freebet-wrapper">
                        <AsyncFreebetPanel freebetId={freebetId} />
                    </div>
                )}

                <UpcomingMatchList
                    isMedium={isMedium}
                    isSmall={isSmall}
                    matchIds={matchIdsWithVisibilityFlags || matchIds}
                    /* for pagination */
                    visibilityInitRef={visibilityInitRef}
                    handleVisibilityChange={handleVisibilityChange}
                />
            </PaginationWrapper>
        );
    }
}

const makeMapStateToProps = (
    state: State,
    ownProps: RouteComponentProps
): MappedCommonProps => {
    const {
        hasNext,
        minPage,
        currentPage,
        maxPage,
        limitPerPage,
    } = selectPaginationByLocation(state, ownProps);
    const categoryData = selectCategoryByLocation(state, ownProps);

    return {
        lessThanOrEqual: makeViewportSelector('lessThanOrEqual')(
            state,
            ownProps
        ),
        categoryData,
        hasNext,
        pages: {
            minPage,
            currentPage,
            maxPage,
            limitPerPage,
        },
        direction: selectDirection(state),
        isLoadInitial: selectIsInitialLoad(state),
    };
};

const mapDispatchToProps: MappedActions = {
    dropPaginationState,
    setMinAndMaxPages,
};

export default compose<React.ComponentClass<{}>>(
    withRouter,
    connect<MappedCommonProps, MappedActions, RouteComponentProps>(
        makeMapStateToProps,
        mapDispatchToProps
    ),
    withPagination,
    withBettingReadyIndicatorContext
)(Upcoming);
